import { useQuery } from "@apollo/client";
import {
  ArrowSmRightIcon,
  ChevronDownIcon,
  ExternalLinkIcon,
} from "@heroicons/react/outline";
import { compact, uniqBy } from "lodash";
import { useCallback, useMemo } from "react";
import {
  AssessmentDeliveryState,
  AssessmentState,
  AssessmentType,
  ComplianceProgramCalibrationType,
  GetActiveReviewsActionsQuery,
  GetActiveReviewsActionsQueryVariables,
} from "types/graphql-schema";

import ComplianceProgramActionItem from "@apps/programs/components/compliance-program-action-item";
import ComplianceProgramActionItemFor1on1 from "@apps/programs/components/compliance-program-action-item-for-1-on-1";
import ComplianceProgramActionItemForAssessment from "@apps/programs/components/compliance-program-action-item-for-assessment";
import ComplianceProgramActionItemForCalibration from "@apps/programs/components/compliance-program-action-item-for-calibration";
import ComplianceProgramActionItemForNomination from "@apps/programs/components/compliance-program-action-item-for-nomination";
import { sortByCompletedItemsLast } from "@apps/programs/components/compliance-program-actions-helpers";
import { ProgramActionToDoStateFilter } from "@apps/programs/components/compliance-program-constants";
import {
  ComplianceProgramActionAssessmentItemProps,
  ComplianceProgramActionBasicItemProps,
  getAssessmentItemsForTemplate,
  getOneononeItemsFromComplianceProgram,
  mapAssessmentDeliveryToItem,
  mapAssessmentForNominationToItem,
  mapCalibrationGroupsToItem,
} from "@apps/programs/helpers";
import { useProgramsSection } from "@apps/programs/programs-section-context";
import useLabel from "@apps/use-label/use-label";
import useUiPreferenceCache from "@apps/use-ui-preference-cache/use-ui-preference-cache";
import {
  currentOrganizationVar,
  currentUserVar,
  isAdminVar,
} from "@cache/cache";
import Button from "@components/button/button";
import Layout from "@components/layout/layout";
import AppLink from "@components/link/link";
import AppPopover from "@components/popover/app-popover";
import {
  ToggleButtonGroup,
  ToggleButtonGroupTheme,
} from "@components/toggle-button-group/toggle-button-group";
import Tooltip from "@components/tooltip/tooltip";
import { getAssessmentTypeLabel } from "@helpers/constants";
import { classNames } from "@helpers/css";
import {
  assertEdgesNonNull,
  assertEdgesNonNullWithStringId,
} from "@helpers/helpers";

import getActiveReviewsActionsQuery from "../graphql/get-active-reviews-actions-query";

export const ComplianceProgramItemProgramLink = ({
  program,
}: {
  program?: null | {
    id: number;
    title: string;
    canReadDetails?: { permission: boolean };
  };
}) => {
  const { noun, basePath } = useProgramsSection();
  return program ? (
    program.canReadDetails?.permission ? (
      <AppLink
        to={`${basePath}/${program.id}/progress`}
        className="text-xs text-gray-500 hover:underline"
      >
        <Tooltip text={`View ${noun.toLowerCase()} progress`}>
          <span className="flex items-center gap-1">
            {program.title} <ExternalLinkIcon className="w-3 h-3" />
          </span>
        </Tooltip>
      </AppLink>
    ) : (
      <div className="text-xs text-gray-500">{program.title}</div>
    )
  ) : null;
};

const AssessmentsHomepageActiveReviews = () => {
  const { basePath } = useProgramsSection();
  const { uiPreferenceCache, saveUiPreference } = useUiPreferenceCache();
  const currentUser = currentUserVar();
  const isAdmin = isAdminVar();
  const handleChangeActiveReviewFilter = useCallback(
    (filter: ProgramActionToDoStateFilter) => {
      saveUiPreference({ programActionsFilter: filter });
    },
    [saveUiPreference],
  );

  const label = useLabel();
  const currentOrganization = currentOrganizationVar();
  const reportIds = useMemo(
    () =>
      assertEdgesNonNull(currentUser.directReports).map((report) => report.id),
    [currentUser.directReports],
  );
  const hasReports = reportIds.length > 0;

  const { data, loading } = useQuery<
    GetActiveReviewsActionsQuery,
    GetActiveReviewsActionsQueryVariables
  >(getActiveReviewsActionsQuery, {
    variables: {
      organizationId: currentOrganization.id,
      currentUserId: currentUser.id,
      nominationTargetId: null,
      deliveryTargetIds: [...reportIds, currentUser.id],
    },
  });

  const filterToDoState = useCallback(
    (item: ComplianceProgramActionBasicItemProps) =>
      uiPreferenceCache.programActionsFilter ===
        ProgramActionToDoStateFilter.All ||
      (uiPreferenceCache.programActionsFilter ===
        ProgramActionToDoStateFilter.ToDo &&
        !item.statusCompleted),
    [uiPreferenceCache.programActionsFilter],
  );
  const filterIsAwaitingAdminApproval = useCallback(
    (item: ComplianceProgramActionAssessmentItemProps) =>
      item.assessmentState === AssessmentState.Submitted &&
      item.assessmentDeliveryState ===
        AssessmentDeliveryState.AwaitingAdminApproval,
    [],
  );
  const filterIsNotAwaitingAdminApproval = useCallback(
    (item: ComplianceProgramActionAssessmentItemProps) =>
      !filterIsAwaitingAdminApproval(item),
    [filterIsAwaitingAdminApproval],
  );
  const filterForCurrentUser = useCallback(
    (item: ComplianceProgramActionBasicItemProps) =>
      item.user?.id === currentUser.id && filterToDoState(item),
    [currentUser.id, filterToDoState],
  );
  const filterForDirectReports = useCallback(
    (item: ComplianceProgramActionBasicItemProps) =>
      reportIds.length > 0 &&
      item.user?.id &&
      reportIds.includes(item.user.id) &&
      filterToDoState(item),
    [reportIds, filterToDoState],
  );
  const filterForOtherUsers = useCallback(
    (item: ComplianceProgramActionBasicItemProps) =>
      item.user?.id &&
      !reportIds.includes(item.user.id) &&
      item.user.id !== currentUser.id &&
      filterToDoState(item),
    [currentUser.id, reportIds, filterToDoState],
  );

  const compliancePrograms = useMemo(
    () =>
      data?.compliancePrograms
        ? assertEdgesNonNull(data.compliancePrograms).filter(
            (program) =>
              program.performanceAssessmentTemplate ||
              program.peerAssessmentTemplate ||
              program.managerAssessmentTemplate,
          )
        : [],
    [data],
  );
  const performanceAssessmentCompliancePrograms = useMemo(
    () =>
      compliancePrograms.filter(
        (program) => program.performanceAssessmentTemplate,
      ),
    [compliancePrograms],
  );
  const managerEffectivenessCompliancePrograms = useMemo(
    () =>
      compliancePrograms.filter((program) => program.managerAssessmentTemplate),
    [compliancePrograms],
  );
  const peerAssessmentCompliancePrograms = useMemo(
    () =>
      compliancePrograms.filter((program) => program.peerAssessmentTemplate),
    [compliancePrograms],
  );
  const performanceAssessmentItems =
    performanceAssessmentCompliancePrograms.reduce((memo, program) => {
      if (!program.performanceAssessmentTemplate) {
        return memo;
      }
      const items = getAssessmentItemsForTemplate(
        program,
        program.performanceAssessmentTemplate,
        currentUser.id,
      );
      return memo.concat(items);
    }, [] as ComplianceProgramActionAssessmentItemProps[]);
  const myPerformanceAssessmentItems = performanceAssessmentItems
    .filter(filterForCurrentUser)
    .filter(filterIsNotAwaitingAdminApproval);
  const directReportsPerformanceAssessmentItems = performanceAssessmentItems
    .filter(filterForDirectReports)
    .filter(filterIsNotAwaitingAdminApproval);
  const otherPerformanceAssessmentItems = performanceAssessmentItems
    .filter(filterForOtherUsers)
    .filter(filterIsNotAwaitingAdminApproval);
  const myPerformanceAssessmentAwaitingAdminApprovalItems =
    performanceAssessmentItems
      .filter(filterForCurrentUser)
      .filter(filterIsAwaitingAdminApproval);
  const directReportsPerformanceAssessmentAwaitingAdminApprovalItems =
    performanceAssessmentItems
      .filter(filterForDirectReports)
      .filter(filterIsAwaitingAdminApproval);
  const otherPerformanceAssessmentAwaitingAdminApprovalItems =
    performanceAssessmentItems
      .filter(filterForOtherUsers)
      .filter(filterIsAwaitingAdminApproval);

  const managerAssessmentItems = managerEffectivenessCompliancePrograms.reduce(
    (memo, program) => {
      if (!program.managerAssessmentTemplate) {
        return memo;
      }
      const items = getAssessmentItemsForTemplate(
        program,
        program.managerAssessmentTemplate,
        currentUser.id,
      );
      return memo.concat(items);
    },
    [] as ComplianceProgramActionAssessmentItemProps[],
  );
  const myManagerAssessmentItems =
    managerAssessmentItems.filter(filterForCurrentUser);
  const directReportsManagerAssessmentItems = managerAssessmentItems.filter(
    filterForDirectReports,
  );
  const otherManagerAssessmentItems =
    managerAssessmentItems.filter(filterForOtherUsers);
  const peerAssessmentItems = peerAssessmentCompliancePrograms.reduce(
    (memo, program) => {
      if (!program.peerAssessmentTemplate) {
        return memo;
      }
      const items = getAssessmentItemsForTemplate(
        program,
        program.peerAssessmentTemplate,
        currentUser.id,
      );
      return memo.concat(items);
    },
    [] as ComplianceProgramActionAssessmentItemProps[],
  );
  const myPeerAssessmentItems =
    peerAssessmentItems.filter(filterForCurrentUser);
  const directReportsPeerAssessmentItems = peerAssessmentItems.filter(
    filterForDirectReports,
  );
  const otherPeerAssessmentItems =
    peerAssessmentItems.filter(filterForOtherUsers);

  const deliveries = data?.assessmentDeliveries
    ? assertEdgesNonNull(data.assessmentDeliveries)
    : [];

  const allDeliveryItems = deliveries.map((delivery) =>
    mapAssessmentDeliveryToItem(currentUser.id, delivery),
  );
  const myDeliveries = allDeliveryItems.filter(filterForCurrentUser);
  const directReportsDeliveries = allDeliveryItems.filter(
    filterForDirectReports,
  );
  const otherDeliveries = allDeliveryItems.filter(filterForOtherUsers);

  const assessmentsOpenForNominations = (
    data?.assessmentsOpenForNominations
      ? assertEdgesNonNullWithStringId(data.assessmentsOpenForNominations)
      : []
  ).map((assessmentsOpenForNomination) =>
    mapAssessmentForNominationToItem(
      currentUser.id,
      assessmentsOpenForNomination,
    ),
  );
  const myNominations =
    assessmentsOpenForNominations.filter(filterForCurrentUser);
  const directReportsNominations = assessmentsOpenForNominations.filter(
    filterForDirectReports,
  );
  const otherNominations =
    assessmentsOpenForNominations.filter(filterForOtherUsers);

  const oneononeItems = compliancePrograms.reduce((memo, complianceProgram) => {
    return [
      ...memo,
      ...getOneononeItemsFromComplianceProgram(
        complianceProgram,
        currentUser.id,
      ),
    ];
  }, [] as any[]);
  const otherOneononeItems = oneononeItems.filter(filterForOtherUsers);
  const myOneononeItems = oneononeItems.filter(filterForCurrentUser);
  const directReportsOneononeItems = oneononeItems.filter(
    filterForDirectReports,
  );

  // Transform calibration groups using helper and separate by type
  const calibrationGroupItems = useMemo(
    () =>
      data?.allCalibrationGroups
        ? mapCalibrationGroupsToItem(data.allCalibrationGroups)
        : [],
    [data?.allCalibrationGroups],
  );

  const { preReviewCalibrationGroups, postReviewCalibrationGroups } =
    useMemo(() => {
      return {
        preReviewCalibrationGroups: calibrationGroupItems.filter(
          (item) =>
            item.group.complianceProgram.calibration ===
            ComplianceProgramCalibrationType.PreCalibration,
        ),
        postReviewCalibrationGroups: calibrationGroupItems.filter(
          (item) =>
            item.group.complianceProgram.calibration ===
            ComplianceProgramCalibrationType.PostCalibration,
        ),
      };
    }, [calibrationGroupItems]);

  const myItemsCount =
    myNominations.length +
    myDeliveries.length +
    myPerformanceAssessmentItems.length +
    myPerformanceAssessmentAwaitingAdminApprovalItems.length +
    myManagerAssessmentItems.length +
    myPeerAssessmentItems.length;
  const directReportsItemsCount =
    directReportsNominations.length +
    directReportsDeliveries.length +
    directReportsPerformanceAssessmentItems.length +
    directReportsPerformanceAssessmentAwaitingAdminApprovalItems.length +
    directReportsManagerAssessmentItems.length +
    directReportsPeerAssessmentItems.length;
  const otherItemsCount =
    otherPerformanceAssessmentItems.length +
    otherPerformanceAssessmentAwaitingAdminApprovalItems.length +
    otherManagerAssessmentItems.length +
    otherPeerAssessmentItems.length +
    otherDeliveries.length +
    otherNominations.length +
    preReviewCalibrationGroups.length +
    postReviewCalibrationGroups.length;

  const programs: { id: number; title: string }[] = uniqBy(
    compact([
      ...performanceAssessmentCompliancePrograms,
      ...managerEffectivenessCompliancePrograms,
      ...peerAssessmentCompliancePrograms,
      ...assessmentsOpenForNominations.map(
        ({ complianceProgram }) => complianceProgram,
      ),
      ...deliveries.map(({ complianceProgram }) => complianceProgram),
    ]),
    "id",
  );
  return (
    <>
      <div className="flex py-8">
        <div className="flex justify-between w-full">
          <ToggleButtonGroup<ProgramActionToDoStateFilter>
            theme={ToggleButtonGroupTheme.mergedLightBlueButtons}
            buttons={[
              {
                title: ProgramActionToDoStateFilter.All,
                value: ProgramActionToDoStateFilter.All,
                active:
                  uiPreferenceCache.programActionsFilter ===
                  ProgramActionToDoStateFilter.All,
              },
              {
                title: ProgramActionToDoStateFilter.ToDo,
                value: ProgramActionToDoStateFilter.ToDo,
                active:
                  uiPreferenceCache.programActionsFilter ===
                  ProgramActionToDoStateFilter.ToDo,
              },
            ]}
            onClick={(button) =>
              handleChangeActiveReviewFilter(
                button?.value || ProgramActionToDoStateFilter.All,
              )
            }
          />
          {(isAdmin || hasReports) && (
            <AppPopover
              options={{
                className: classNames(
                  AppPopover.className,
                  "w-96 overflow-y-auto max-h-64",
                ),
              }}
              placement="bottom-end"
              content={
                <div className="p-4">
                  <div className="text-sm font-medium mb-2">
                    {label("review", { capitalize: true, pluralize: true })}
                  </div>
                  <div className="flex flex-col divide-y border-t border-b">
                    {programs.map((program) => (
                      <AppLink
                        key={program.id}
                        to={`${basePath}/${program.id}/progress`}
                        className="py-2 px-1 text-sm text-gray-500 hover:bg-gray-50 flex items-center justify-between"
                      >
                        {program.title}
                        <ArrowSmRightIcon className="w-4 h-4" />
                      </AppLink>
                    ))}
                  </div>
                </div>
              }
            >
              <AppPopover.Button
                as={Button}
                className="flex items-center gap-1"
                disabled={programs.length === 0}
              >
                View {label("review")} progress{" "}
                <ChevronDownIcon className="w-3 h-3 text-gray-500" />
              </AppPopover.Button>
            </AppPopover>
          )}
        </div>
      </div>

      <Layout.MainSection
        title={`My ${label("review", {
          capitalize: true,
          pluralize: true,
        })}`}
        titleClassName="text-base font-semibold text-gray-900"
      >
        <Layout.MainSubSection
          loading={loading}
          emptyPlaceholder={
            myItemsCount === 0
              ? `There are no active ${label("review", {
                  pluralize: true,
                })} for you`
              : null
          }
        >
          <Layout.MainSubSectionList>
            {sortByCompletedItemsLast(myNominations).map((item) => (
              <ComplianceProgramActionItemForNomination
                key={item.key}
                item={item}
              >
                <ComplianceProgramItemProgramLink
                  program={item.complianceProgram}
                />
              </ComplianceProgramActionItemForNomination>
            ))}
            {sortByCompletedItemsLast(myDeliveries).map(({ key, ...item }) => (
              <ComplianceProgramActionItem key={key} {...item}>
                <ComplianceProgramItemProgramLink
                  program={item.complianceProgram}
                />
              </ComplianceProgramActionItem>
            ))}
            {sortByCompletedItemsLast(myPerformanceAssessmentItems).map(
              (item) => (
                <ComplianceProgramActionItemForAssessment
                  key={item.key}
                  item={item}
                >
                  <ComplianceProgramItemProgramLink
                    program={item.complianceProgram}
                  />
                </ComplianceProgramActionItemForAssessment>
              ),
            )}
            {sortByCompletedItemsLast(
              myPerformanceAssessmentAwaitingAdminApprovalItems,
            ).map((item) => (
              <ComplianceProgramActionItemForAssessment
                key={item.key}
                item={item}
              >
                <ComplianceProgramItemProgramLink
                  program={item.complianceProgram}
                />
              </ComplianceProgramActionItemForAssessment>
            ))}
            {sortByCompletedItemsLast(myManagerAssessmentItems).map((item) => (
              <ComplianceProgramActionItemForAssessment
                key={item.key}
                item={item}
              >
                <ComplianceProgramItemProgramLink
                  program={item.complianceProgram}
                />
              </ComplianceProgramActionItemForAssessment>
            ))}
            {sortByCompletedItemsLast(myPeerAssessmentItems).map((item) => (
              <ComplianceProgramActionItemForAssessment
                key={item.key}
                item={item}
              >
                <ComplianceProgramItemProgramLink
                  program={item.complianceProgram}
                />
              </ComplianceProgramActionItemForAssessment>
            ))}

            {sortByCompletedItemsLast(myOneononeItems).map((item) => (
              <ComplianceProgramActionItemFor1on1 key={item.id} item={item}>
                <ComplianceProgramItemProgramLink
                  program={item.complianceProgram}
                />
              </ComplianceProgramActionItemFor1on1>
            ))}
          </Layout.MainSubSectionList>
        </Layout.MainSubSection>
      </Layout.MainSection>

      {reportIds.length > 0 && !loading && (
        <Layout.MainSection
          title={`Actions for Direct Reports`}
          titleClassName="text-base font-semibold text-gray-900"
        >
          {directReportsItemsCount === 0 && (
            <Layout.MainSubSectionEmpty>
              There are no active{" "}
              {label("review", {
                pluralize: true,
              })}{" "}
              for your direct reports.
            </Layout.MainSubSectionEmpty>
          )}

          {directReportsNominations.length > 0 && (
            <Layout.MainSubSection
              title={`${getAssessmentTypeLabel(AssessmentType.Peer)} ${label(
                "review",
                { capitalize: true, titleCase: true },
              )} Nominations`}
              collapsible
              defaultIsExpanded
            >
              <Layout.MainSubSectionList>
                {sortByCompletedItemsLast(directReportsNominations).map(
                  (item) => (
                    <ComplianceProgramActionItemForNomination
                      key={item.key}
                      item={item}
                    >
                      <ComplianceProgramItemProgramLink
                        program={item.complianceProgram}
                      />
                    </ComplianceProgramActionItemForNomination>
                  ),
                )}
              </Layout.MainSubSectionList>
            </Layout.MainSubSection>
          )}
          {directReportsPerformanceAssessmentItems.length > 0 && (
            <Layout.MainSubSection
              title={`${getAssessmentTypeLabel(
                AssessmentType.Performance,
              )} ${label("review", { pluralize: true, titleCase: true })}`}
              collapsible
              defaultIsExpanded
            >
              <Layout.MainSubSectionList>
                {sortByCompletedItemsLast(
                  directReportsPerformanceAssessmentItems,
                ).map((item) => (
                  <ComplianceProgramActionItemForAssessment
                    key={item.key}
                    item={item}
                  >
                    <ComplianceProgramItemProgramLink
                      program={item.complianceProgram}
                    />
                  </ComplianceProgramActionItemForAssessment>
                ))}
              </Layout.MainSubSectionList>
            </Layout.MainSubSection>
          )}
          {directReportsPerformanceAssessmentAwaitingAdminApprovalItems.length >
            0 && (
            <Layout.MainSubSection
              title={`${getAssessmentTypeLabel(
                AssessmentType.Performance,
              )} ${label("review", {
                pluralize: true,
                titleCase: true,
              })} Awaiting Admin Approval`}
              collapsible
              defaultIsExpanded
            >
              <Layout.MainSubSectionList>
                {sortByCompletedItemsLast(
                  directReportsPerformanceAssessmentAwaitingAdminApprovalItems,
                ).map((item) => (
                  <ComplianceProgramActionItemForAssessment
                    key={item.key}
                    item={item}
                  >
                    <ComplianceProgramItemProgramLink
                      program={item.complianceProgram}
                    />
                  </ComplianceProgramActionItemForAssessment>
                ))}
              </Layout.MainSubSectionList>
            </Layout.MainSubSection>
          )}
          {directReportsManagerAssessmentItems.length > 0 && (
            <Layout.MainSubSection
              title={`${getAssessmentTypeLabel(AssessmentType.Manager)} ${label(
                "review",
                { pluralize: true, titleCase: true },
              )}`}
              collapsible
              defaultIsExpanded
            >
              <Layout.MainSubSectionList>
                {sortByCompletedItemsLast(
                  directReportsManagerAssessmentItems,
                ).map((item) => (
                  <ComplianceProgramActionItemForAssessment
                    key={item.key}
                    item={item}
                  >
                    <ComplianceProgramItemProgramLink
                      program={item.complianceProgram}
                    />
                  </ComplianceProgramActionItemForAssessment>
                ))}
              </Layout.MainSubSectionList>
            </Layout.MainSubSection>
          )}
          {directReportsPeerAssessmentItems.length > 0 && (
            <Layout.MainSubSection
              title={`${getAssessmentTypeLabel(AssessmentType.Peer)} ${label(
                "review",
                { pluralize: true, titleCase: true },
              )}`}
              collapsible
              defaultIsExpanded
            >
              <Layout.MainSubSectionList>
                {sortByCompletedItemsLast(directReportsPeerAssessmentItems).map(
                  (item) => (
                    <ComplianceProgramActionItemForAssessment
                      key={item.key}
                      item={item}
                    >
                      <ComplianceProgramItemProgramLink
                        program={item.complianceProgram}
                      />
                    </ComplianceProgramActionItemForAssessment>
                  ),
                )}
              </Layout.MainSubSectionList>
            </Layout.MainSubSection>
          )}
          {directReportsDeliveries.length > 0 && (
            <Layout.MainSubSection
              title={`${label("review", {
                capitalize: true,
                pluralize: true,
              })} to Deliver`}
              collapsible
              defaultIsExpanded
            >
              <Layout.MainSubSectionList>
                {sortByCompletedItemsLast(directReportsDeliveries).map(
                  ({ key, ...item }) => (
                    <ComplianceProgramActionItem key={key} {...item}>
                      <ComplianceProgramItemProgramLink
                        program={item.complianceProgram}
                      />
                    </ComplianceProgramActionItem>
                  ),
                )}
              </Layout.MainSubSectionList>
            </Layout.MainSubSection>
          )}
          {directReportsOneononeItems.length > 0 && (
            <Layout.MainSubSection
              title={`${label("1-on-1", {
                capitalize: true,
                titleCase: true,
              })} Meetings`}
              collapsible
              defaultIsExpanded
            >
              <Layout.MainSubSectionList>
                {sortByCompletedItemsLast(directReportsOneononeItems).map(
                  (item) => (
                    <ComplianceProgramActionItemFor1on1
                      key={item.key}
                      item={item}
                    >
                      <ComplianceProgramItemProgramLink
                        program={item.complianceProgram}
                      />
                    </ComplianceProgramActionItemFor1on1>
                  ),
                )}
              </Layout.MainSubSectionList>
            </Layout.MainSubSection>
          )}
        </Layout.MainSection>
      )}

      {!loading && (
        <Layout.MainSection
          title={`Actions for Others`}
          titleClassName="text-base font-semibold text-gray-900"
        >
          {otherItemsCount === 0 && (
            <Layout.MainSubSectionEmpty>
              There are no active{" "}
              {label("review", {
                pluralize: true,
              })}{" "}
              for your peers or managers.
            </Layout.MainSubSectionEmpty>
          )}

          {otherPerformanceAssessmentItems.length > 0 && (
            <Layout.MainSubSection
              title={`${getAssessmentTypeLabel(
                AssessmentType.Performance,
              )} ${label("review", {
                capitalize: true,
                pluralize: true,
              })}`}
              collapsible
              defaultIsExpanded
            >
              <Layout.MainSubSectionList>
                {sortByCompletedItemsLast(otherPerformanceAssessmentItems).map(
                  (item) => (
                    <ComplianceProgramActionItemForAssessment
                      key={item.key}
                      item={item}
                    >
                      <ComplianceProgramItemProgramLink
                        program={item.complianceProgram}
                      />
                    </ComplianceProgramActionItemForAssessment>
                  ),
                )}
              </Layout.MainSubSectionList>
            </Layout.MainSubSection>
          )}
          {otherPerformanceAssessmentAwaitingAdminApprovalItems.length > 0 && (
            <Layout.MainSubSection
              title={`${getAssessmentTypeLabel(
                AssessmentType.Performance,
              )} ${label("review", {
                capitalize: true,
                pluralize: true,
              })} Awaiting Admin Approval`}
              collapsible
              defaultIsExpanded
            >
              <Layout.MainSubSectionList>
                {sortByCompletedItemsLast(
                  otherPerformanceAssessmentAwaitingAdminApprovalItems,
                ).map((item) => (
                  <ComplianceProgramActionItemForAssessment
                    key={item.key}
                    item={item}
                  >
                    <ComplianceProgramItemProgramLink
                      program={item.complianceProgram}
                    />
                  </ComplianceProgramActionItemForAssessment>
                ))}
              </Layout.MainSubSectionList>
            </Layout.MainSubSection>
          )}
          {otherManagerAssessmentItems.length > 0 && (
            <Layout.MainSubSection
              title={`${getAssessmentTypeLabel(AssessmentType.Manager)} ${label(
                "review",
                {
                  capitalize: true,
                  pluralize: true,
                },
              )}`}
              collapsible
              defaultIsExpanded
            >
              <Layout.MainSubSectionList>
                {sortByCompletedItemsLast(otherManagerAssessmentItems).map(
                  (item) => (
                    <ComplianceProgramActionItemForAssessment
                      key={item.key}
                      item={item}
                    >
                      <ComplianceProgramItemProgramLink
                        program={item.complianceProgram}
                      />
                    </ComplianceProgramActionItemForAssessment>
                  ),
                )}
              </Layout.MainSubSectionList>
            </Layout.MainSubSection>
          )}
          {otherPeerAssessmentItems.length > 0 && (
            <Layout.MainSubSection
              title={`${getAssessmentTypeLabel(AssessmentType.Peer)} ${label(
                "review",
                {
                  capitalize: true,
                  pluralize: true,
                },
              )}`}
              collapsible
              defaultIsExpanded
            >
              <Layout.MainSubSectionList>
                {sortByCompletedItemsLast(otherPeerAssessmentItems).map(
                  (item) => (
                    <ComplianceProgramActionItemForAssessment
                      key={item.key}
                      item={item}
                    >
                      <ComplianceProgramItemProgramLink
                        program={item.complianceProgram}
                      />
                    </ComplianceProgramActionItemForAssessment>
                  ),
                )}
              </Layout.MainSubSectionList>
            </Layout.MainSubSection>
          )}
          {otherDeliveries.length > 0 && (
            <Layout.MainSubSection
              title={`${label("review", {
                capitalize: true,
                pluralize: true,
              })} to Deliver`}
              collapsible
              defaultIsExpanded
            >
              <Layout.MainSubSectionList>
                {sortByCompletedItemsLast(otherDeliveries).map(
                  ({ key, ...item }) => (
                    <ComplianceProgramActionItem key={key} {...item}>
                      <ComplianceProgramItemProgramLink
                        program={item.complianceProgram}
                      />
                    </ComplianceProgramActionItem>
                  ),
                )}
              </Layout.MainSubSectionList>
            </Layout.MainSubSection>
          )}
          {/* Manager/Peer specific delivery sections removed in favor of single combined list */}
          {otherNominations.length > 0 && (
            <Layout.MainSubSection
              title={`${getAssessmentTypeLabel(AssessmentType.Peer)} ${label(
                "review",
                { capitalize: true, titleCase: true },
              )} Nominations`}
              collapsible
              defaultIsExpanded
            >
              <Layout.MainSubSectionList>
                {sortByCompletedItemsLast(otherNominations).map((item) => (
                  <ComplianceProgramActionItemForNomination
                    key={item.key}
                    item={item}
                  >
                    <ComplianceProgramItemProgramLink
                      program={item.complianceProgram}
                    />
                  </ComplianceProgramActionItemForNomination>
                ))}
              </Layout.MainSubSectionList>
            </Layout.MainSubSection>
          )}

          {otherOneononeItems.length > 0 && (
            <Layout.MainSubSection
              title={`${label("1-on-1", {
                capitalize: true,
                titleCase: true,
              })} Meetings`}
              collapsible
              defaultIsExpanded
            >
              <Layout.MainSubSectionList>
                {sortByCompletedItemsLast(otherOneononeItems).map((item) => (
                  <ComplianceProgramActionItemFor1on1
                    key={item.key}
                    item={item}
                  >
                    <ComplianceProgramItemProgramLink
                      program={item.complianceProgram}
                    />
                  </ComplianceProgramActionItemFor1on1>
                ))}
              </Layout.MainSubSectionList>
            </Layout.MainSubSection>
          )}

          {/* Pre-Review Calibration Section */}
          {preReviewCalibrationGroups.length > 0 && (
            <Layout.MainSubSection
              title={
                <div className="flex items-center gap-2">
                  {`Pre-${label("review", {
                    capitalize: true,
                  })} Calibration`}
                  <span className="text-xs text-gray-500 bg-gray-100 rounded px-2 py-0.5 font-normal">
                    {preReviewCalibrationGroups.length}
                  </span>
                </div>
              }
              collapsible
              defaultIsExpanded
            >
              <Layout.MainSubSectionList>
                {preReviewCalibrationGroups.map((item) => (
                  <ComplianceProgramActionItemForCalibration
                    key={item.key}
                    item={item}
                  >
                    <ComplianceProgramItemProgramLink
                      program={item.group.complianceProgram}
                    />
                  </ComplianceProgramActionItemForCalibration>
                ))}
              </Layout.MainSubSectionList>
            </Layout.MainSubSection>
          )}

          {/* Post-Review Calibration Section */}
          {postReviewCalibrationGroups.length > 0 && (
            <Layout.MainSubSection
              title={
                <div className="flex items-center gap-2">
                  {`Post-${label("review", {
                    capitalize: true,
                  })} Calibration`}
                  <span className="text-xs text-gray-500 bg-gray-100 rounded px-2 py-0.5 font-normal">
                    {postReviewCalibrationGroups.length}
                  </span>
                </div>
              }
              collapsible
              defaultIsExpanded
            >
              <Layout.MainSubSectionList>
                {postReviewCalibrationGroups.map((item) => (
                  <ComplianceProgramActionItemForCalibration
                    key={item.key}
                    item={item}
                  >
                    <ComplianceProgramItemProgramLink
                      program={item.group.complianceProgram}
                    />
                  </ComplianceProgramActionItemForCalibration>
                ))}
              </Layout.MainSubSectionList>
            </Layout.MainSubSection>
          )}
        </Layout.MainSection>
      )}
    </>
  );
};

export default AssessmentsHomepageActiveReviews;
